import React from 'react'
import { useTranslation } from 'react-i18next'
import { Line } from 'react-chartjs-2'
import { defaultTheme } from '@dataplace.ai/ui-components/themes/defaultTheme'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import { processChartData } from './functions'
import { DataRow, DataTitle } from './People.styles'
import { CHART_LABELS } from './constants'
import { StyledWrapper, TitleWrapper } from '../SharedOverview.styles'
import { NoDataGuard } from '../components/NoDataGuard'
import { NO_OVERVIEW_DATA } from '../constants'

export interface SeriesItem {
  k: number
  approxRadiusM: number
  total: number
}
interface PeopleProps {
  series: SeriesItem[]
}

export type PeopleData = PeopleProps['series'] | typeof NO_OVERVIEW_DATA
const TILE_TITLE_KEY = 'placeme.tile.people'

function PEOPLE({ series }: PeopleProps): JSX.Element {
  const { t } = useTranslation()
  const { isPrinting } = useCheckIfPrinting()
  const {
    data: chartData,
    labels,
    pointRadius,
    pointIndex,
    endIndex,
  } = processChartData(series)

  return (
    <StyledWrapper>
      <TitleWrapper>
        {t(TILE_TITLE_KEY)}
      </TitleWrapper>
      <DataRow>
        <DataTitle>{t('placeme.overview.people.subtitle')}</DataTitle>
      </DataRow>
      <Line
        data={{
          labels,
          datasets: [{
            data: chartData,
            borderColor: defaultTheme.palette.electricBlue,
            pointRadius,
            fill: true,
            backgroundColor: 'rgba(74, 58, 255, 0)',
            pointBackgroundColor: defaultTheme.palette.electricBlue,
          }],
        }}
        height={isPrinting ? 100 : 200}
        options={{
          legend: {
            display: false, // Hide the legend
          },
          scales: {
            xAxes: [{
              gridLines: {
                display: false, // Remove vertical grid lines
                drawBorder: false,
              },
              ticks: {
                display: true,
                maxRotation: 0,
                minRotation: 0,
                callback: (value: unknown, index: number) => {
                  if (index === 0) return CHART_LABELS.START
                  if (index === pointIndex) return CHART_LABELS.POINT
                  if (index === endIndex) return CHART_LABELS.END
                  return ''
                },
              },
            }],
            yAxes: [{
              position: 'left', // Move y-axis labels to the left
              gridLines: {
                display: true, // Show horizontal grid lines
                drawBorder: false,
                borderDash: [5, 5], // Dashed grid lines
              },
              ticks: {
                display: true,
              },
            }],
          },
          plugins: {
            datalabels: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              display: (context: any) => context.dataIndex === pointIndex,
              color: defaultTheme.palette.electricBlue,
              font: {
                size: 16,
                weight: defaultTheme.typography.big.pt_18_semibold.fontWeight,
              },
              anchor: 'end',
              align: 'top',
              offset: 0,
              formatter: (value: number) => value.toString(),
            },
          },
          tooltips: {
            enabled: true,
            mode: 'nearest',
            intersect: false,
            displayColors: false,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            filter: (tooltipItem: any) =>
              tooltipItem.index === 0
              || tooltipItem.index === endIndex,
            callbacks: {
              title: () => '', // Hides the x-axis label
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              label: (tooltipItem: any) =>
                tooltipItem.yLabel.toString(),
            },
          },
        }}
        width={isPrinting ? 300 : 400}
      />
    </StyledWrapper>
  )
}

export function People({ data }: {data: PeopleData}): JSX.Element {
  return (
    <NoDataGuard
      component={PEOPLE}
      data={data === NO_OVERVIEW_DATA
        ? data
        : {
          series: data,
        }}
      title={TILE_TITLE_KEY}
    />
  )
}
